import React, { useEffect, useState } from 'react';
import * as S from './Table.styled';
import { Button, Table as TableAnt, Tag } from 'antd';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { ILog, ILogType, IUser } from 'interfaces/main';
import { sortBy } from 'lodash';
import type { FilterValue, SortOrder, SorterResult } from 'antd/es/table/interface';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { useAppSelector } from 'hooks/useStore';

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: string;
	sortOrder?: SortOrder;
	filters?: Record<string, FilterValue>;
}

export interface Props {
	items: ILog[];
	isOwner: boolean;
	onRemove(id: number): void;
}

const Table: React.FC<Props> = ({ items, isOwner, onRemove }) => {
	// Словари
	const { logTypes: allLogTypes } = useAppSelector(LibrariesSelector);

	// Table params
	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 100,
			total: 0,
		},
	});

	useEffect(
		() =>
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: items.length,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[items],
	);

	const emails = sortBy(
		uniq(items.map((item: ILog) => item?.user?.email)).map((email: string) => ({
			text: email,
			value: email,
		})),
		'text',
	);

	const phones = uniq(items.map((item: ILog) => item?.user?.phone)).map((phone: string | null) => ({
		text: phone ? phone : '-',
		value: phone,
	}));

	// Конфиг таблички для Ant Table
	const columns: ColumnsType<ILog> = [
		{
			title: 'Date',
			dataIndex: 'createdAt',
			key: 'createdAt',
			ellipsis: true,
			sorter: (a: ILog, b: ILog) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
			defaultSortOrder: 'descend',
			render: (createdAt: string) => dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss'),
			// width: 150,
		},
		{
			title: 'Type',
			dataIndex: 'logTypeId',
			key: 'logTypeId',
			filters: allLogTypes.map((item: ILogType) => ({
				text: item.name,
				value: item.id,
			})),
			onFilter: (value: number, log: ILog) => log.logTypeId === value,
			render: (typeId: number) =>
				!!typeId ? (
					<Tag bordered={false} color={allLogTypes.find((p: ILogType) => p.id === typeId).antColor}>
						{allLogTypes.find((p: ILogType) => p.id === typeId).title}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Phone',
			dataIndex: 'user',
			key: 'phone',
			ellipsis: true,
			filters: phones,
			onFilter: (value: string, log: ILog) => log.user.phone === value,
			render: (user: IUser) => user?.phone || '-',
		},
		{
			title: 'Email',
			dataIndex: 'user',
			key: 'email',
			ellipsis: true,
			filters: [{ text: '-', value: null }, ...emails],
			onFilter: (value: string, log: ILog) => log?.user?.email === value,
			render: (user: IUser) => user?.email || '-',
		},
		{
			title: 'Details',
			dataIndex: 'details',
			key: 'details',
			ellipsis: false,
			render: (details: string) => details || '-',
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			ellipsis: false,
			render: (user: IUser) => user?.name || '-',
		},
		{
			title: '',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_: string, lot: ILog) => {
				return (
					<S.Actions>
						<Button size="small" disabled={!isOwner} onClick={() => onRemove(lot.id)}>
							delete
						</Button>
					</S.Actions>
				);
			},
		},
	];

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<ILog>,
	) => {
		setTableParams({
			pagination,
			filters,
			...sorter,
		});
	};

	return (
		<S.Wrap>
			<TableAnt
				columns={columns}
				rowKey={(log: ILog) => log.id}
				dataSource={items}
				pagination={tableParams.pagination}
				onChange={handleTableChange}
			/>
		</S.Wrap>
	);
};

export default Table;
